/* eslint-disable  @typescript-eslint/no-explicit-any */
import {Component, Host, h, Element, Prop, Watch, State, Event, type EventEmitter, Method} from '@stencil/core';
import type {BooleanString} from '../../../models/common/types/boolean-string.type';
import type {FacetChildUiModel} from '../../../models/ui/facets/facet-tree-ui.model';
import type {FilterNode} from '../../result-page/facets-build-wrapper/filter-node';
import {cloneObjectWithNoReference} from '../../../utils/helpers/clone-object-with-no-reference';
import type {FilterCheckbox} from '../filter-checkbox/filter-checkbox';
import premium_document from '../../../assets/svg/premium_document.svg';
import {FacetTag} from '../../result-page/facets/subcomponents/facet-tag/facet-tag';
import {FACET_TAG} from '../../result-page/facets/subcomponents/facet-tag/facet-tags';

@Component({
  tag: 'uiaas-search-facet-filters-node',
  styleUrl: 'facet-filters-node.scss',
  shadow: true,
})
export class FacetFiltersNode {
  @Element() el!: HTMLElement;

  @Prop({attribute: 'level'}) readonly level: number;
  @Prop({attribute: 'show-input'}) readonly showInputProp: BooleanString = 'true';
  @Prop({attribute: 'tree-node'}) readonly treeNodeProp!: string;
  @Watch('treeNodeProp')
  onTreeNodeChange(newValue: string) {
      if(newValue) {
        this.onClearValue();
        this.children = cloneObjectWithNoReference(JSON.parse(newValue).children);
      }
  }
  @Prop({attribute: 'show-header'}) readonly showHeaderProp: BooleanString = 'true';
  @Prop({attribute: 'is-root'}) readonly isRootProp!: BooleanString;
  @Prop({attribute: 'collapse-filters-limit'}) readonly collapseFiltersLimit?: string;
  @Prop({attribute: 'labels'}) readonly labels: {'view-more': string, 'view-less': string, 'input-placeholder': string, 'header-facet-label':string};
  @Prop() readonly filterIcon?: string;
  @Prop() readonly mobileView: boolean = false;
  @Prop() readonly rtl: boolean;

  @State() canClear = false;
  @State() collapsed = true;
  @State() localFiltersState: FilterNode[] = [];
  @State() children: FilterNode[] = [];
  @State() showAllFilters = false;

  private treeNode: FilterNode;
  private filterIconData: {showPremium: boolean, filterValue: string} | undefined;
  private isRoot: boolean;
  private showInput: boolean;
  private collapseFiltersLimitNumber: number | null;
  private timer: NodeJS.Timeout;
  private visibleFiltersNumber = parseInt(this.collapseFiltersLimit) || 0;

  @Event({bubbles: false}) filterChange: EventEmitter<FilterNode>;
  @Event({
    eventName: 'insearch:input',
    composed: true,
    cancelable: true,
    bubbles: true
  }) inSearchInputEmitter: EventEmitter<unknown>;

  @Event({
    eventName: 'uiaas:scrollToTop',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    scrollTopEvent: EventEmitter<void>;

  @Method()
  async clearSelected() {
      this.localFiltersState = [];
      for(const element of (this.el.querySelectorAll('filter-checkbox') as unknown as Array<FilterCheckbox>)) {
        (element as any).setSelected(false);
      }
  }

  private inputElement: HTMLInputElement;

  private onInput(event: Event) {
      const value = (this.inputElement as HTMLInputElement).value;

      this.filterChildren(value);
      this.canClear = value.length > 0;

      if(this.timer) {
        clearTimeout(this.timer);
      }

      this.timer = setTimeout(() => {
          this.inSearchInputEmitter.emit({
            event: 'OnSiteSearch',
            siteSearchKeyword: value,
            facetCategory: this.treeNode.value
          });
      }, 2000);
  }

  private onClearValue(): void {
      if(this.inputElement) {
        (this.inputElement as HTMLInputElement).value = '';
      }
      this.canClear = false;
      this.children = cloneObjectWithNoReference(this.treeNode.children);
      this.el.shadowRoot.querySelectorAll('filter-checkbox').forEach((element) => {
          element.classList.remove('filtered-hide');
      });
  }

  componentWillLoad() {
      this.treeNode = JSON.parse(this.treeNodeProp);
      this.children = cloneObjectWithNoReference(this.treeNode.children);
      this.isRoot = this.isRootProp === 'true';
      this.showInput = this.showInputProp === 'true';
      this.collapseFiltersLimitNumber = this.collapseFiltersLimit ? parseInt(this.collapseFiltersLimit) : null;
      if(this.filterIcon) {
        this.filterIconData = JSON.parse(this.filterIcon);
      }
  }

  componentWillUpdate() {
      this.treeNode = JSON.parse(this.treeNodeProp);
      this.showInput = this.showInputProp === 'true';
      this.isRoot = this.isRootProp === 'true';
      this.collapseFiltersLimitNumber = this.collapseFiltersLimit ? parseInt(this.collapseFiltersLimit) : null;
      if(this.filterIcon) {
        this.filterIconData = JSON.parse(this.filterIcon);
      }
  }

  disconnectedCallback() {
      if(this.timer) {
        clearTimeout(this.timer);
      }
  }

  render() {
      const isCollpaseFiltersBtn = (this.collapseFiltersLimitNumber && (this.children.length > this.collapseFiltersLimitNumber));
      const hiddenFiltersCount = isCollpaseFiltersBtn ? this.children.length - this.visibleFiltersNumber : 0;
      return (
        <Host>
          <div class="ffn-wrapper">
            {(this.treeNode.children?.length > 0)
              ? this.renderHeader()
              : ''}
            <div class={`ffn-body-wrapper ${this.collapsed ? '' : 'closed'}`}>
              {(this.showInput) ? <div class={'ffn-filters-input'}>
                <qds-input
                  class="ffn-qds-input"
                  type="search"
                  placeholder={this.labels['input-placeholder']}
                  onQdsInput={(e) => this.onInput(e)}
                  ref={el => this.inputElement = el as HTMLInputElement}
                />
              </div> : ''}
              <div class={'ffn-checkbox-list-wrapper'}>
                {this.renderFormControls()}
              </div>
              {(!this.canClear && isCollpaseFiltersBtn && hiddenFiltersCount)
                ? <div class={'ffn-expand-button'}>
                  <qds-button
                    importance="subdued"
                    class="ffn-view-more-btn"
                    size="small"
                    action={!this.showAllFilters ? 'dropdown' : 'dropdown-close'}
                    onClick={(e) => {
                        e.preventDefault();
                        this.showAllFilters = !this.showAllFilters;
                    }}
                    tabIndex={this.showAllFilters ? 0 : -1}
                    text={this.showAllFilters ? this.labels['view-less'] : `${this.labels['view-more']} (${hiddenFiltersCount})`}
                  >
                  </qds-button>

                </div> : ''}
            </div>
          </div>
          <slot />
        </Host>
      );
  }

  private isHiddenFilter(index: number): boolean {
      const cloneChildren = cloneObjectWithNoReference(this.children);
      const lastReverseCheckedIndex = cloneChildren.reverse().findIndex((f) => f.selected);
      const lastCheckedIndex = (lastReverseCheckedIndex !== -1) ? (cloneChildren.length - (lastReverseCheckedIndex + 1)) : null;
      const collapaseLimitWhenFilterSelected = lastCheckedIndex + 1;
      const collapaseLimit = this.collapseFiltersLimitNumber;
      this.visibleFiltersNumber = collapaseLimitWhenFilterSelected > this.collapseFiltersLimitNumber ? collapaseLimitWhenFilterSelected : this.collapseFiltersLimitNumber;
      return (collapaseLimit && index + 1 > collapaseLimit) && !this.showAllFilters && !this.canClear && index > lastCheckedIndex;
  }

  private renderFormControls() {
      return (this.children.map((filter: FacetChildUiModel, index) => (
        <filter-checkbox
          key={index + filter.uniqueId}
          class={{
            'hidden-filter': this.isHiddenFilter(index),
          }}
          rtl={this.rtl}
          title={filter.displayName}
          selectedProp={filter.selected ? 'true' : 'false'}
          labelProp={`${filter.displayName} <span class="filter-count">(${filter.count})</span>`}
          onSelectChange={(e) => {
              this.filterChange.emit({...filter, ...{selected: e.detail}} as FilterNode);
              this.handleScrollToTop();
          }}
        >
          {this.filterIconData && this.filterIconData.showPremium && this.filterIconData.filterValue === filter.value
            ? <span class="filter-icon" slot="icon" innerHTML={premium_document}></span>
            : '' }
        </filter-checkbox>
      )));
  }

  private handleScrollToTop = ():void => {
      if(!this.mobileView) {
        this.scrollTopEvent.emit();
      }
  };

  private facetTags(facetId: string, facetDidplayName?: string) {
      return [
        <FacetTag
          isLabelVisible={facetId == FACET_TAG.CUSTOMIZATION}
          endDate='2025/01/31'
          label={this.labels['header-facet-label']} />,
        <FacetTag
          isLabelVisible={facetDidplayName == FACET_TAG.COMPLIANCE}
          endDate='2025/04/28'
          label={this.labels['header-facet-label']} />
      ];
  }

  private renderHeader() {
      return (
        <div class={'ffn-header-wrapper'} onClick={() => this.collapsed = !this.collapsed}>
          <div class={'ffn-header-label-wraper'}>
            <h6 class="ffn-header-title">{this.treeNode.displayName}</h6>
            {this.facetTags(this.treeNode.id, this.treeNode.displayName)}
          </div>

          <div>
            <div
              class={{
                'collapse-arrow': true,
                'rotated': this.collapsed
              }}
            ></div>
          </div>
        </div>
      );
  }

  private filterChildren(value: string): void {
      this.children.map((child, index)=> {
          const bool = (new RegExp(`^.*${value}.*$`, 'gi')).test(child.displayName);
          if(!bool) {
            this.el.shadowRoot.querySelectorAll('filter-checkbox')[index].classList.add('filtered-hide');
          } else {
            this.el.shadowRoot.querySelectorAll('filter-checkbox')[index].classList.remove('filtered-hide');
          }
      });
  }
}
